"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useAuth } from "../../contexts/AuthContext"
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import chatService from "../../services/chatService"
import PropTypes from 'prop-types'

const Chat = ({ userType = "client", styles = {} }) => {
  const { user } = useAuth()
  const { conversationId } = useParams()
  const navigate = useNavigate()

  const [messages, setMessages] = useState([])
  const [input, setInput] = useState("")
  const [isTyping, setIsTyping] = useState(false)
  const [conversations, setConversations] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const endRef = useRef(null)
  const typingTimeoutRef = useRef(null)

  const basePath = userType === "client" ? "/client/chat" : "/demenageur/messages"

  const formatDate = (dateString) => {
    if (!dateString) return ''
    try {
      const date = new Date(dateString)
      // Si la date est aujourd'hui, afficher uniquement l'heure
      const now = new Date()
      if (date.toDateString() === now.toDateString()) {
        return format(date, 'HH:mm')
      }
      // Si c'est hier
      const yesterday = new Date(now)
      yesterday.setDate(yesterday.getDate() - 1)
      if (date.toDateString() === yesterday.toDateString()) {
        return `Hier, ${format(date, 'HH:mm')}`
      }
      // Si c'est dans les 7 derniers jours, afficher le jour de la semaine
      const lastWeek = new Date(now)
      lastWeek.setDate(lastWeek.getDate() - 7)
      if (date > lastWeek) {
        return format(date, 'EEEE, HH:mm', { locale: fr })
      }
      // Sinon, afficher la date complète
      return format(date, 'dd/MM/yyyy HH:mm', { locale: fr })
    } catch (error) {
      console.error('Erreur de formatage de date:', error)
      return dateString
    }
  }

  const loadConversations = useCallback(async () => {
    try {
      setIsLoading(true)
      const data = await chatService.getConversations()
      
      // Vérifier que data est un tableau et filtrer les valeurs null/undefined
      if (!Array.isArray(data)) {
        console.error('[Chat] Les données des conversations ne sont pas un tableau:', data)
        setConversations([])
        return
      }
      
      const formattedConversations = data
        .filter(conv => conv != null) // Filtrer les valeurs null/undefined
        .map(conv => {
        const client = {
          id: conv.conv_client_id,
          first_name: conv.client_first_name || conv.client_firstname,
          last_name: conv.client_last_name || conv.client_lastname,
          avatar: conv.client_avatar
        }
        const mover = {
          id: conv.conv_mover_id,
          first_name: conv.mover_first_name || conv.mover_firstname,
          last_name: conv.mover_last_name || conv.mover_lastname,
          company_name: conv.company_name,
          avatar: conv.mover_avatar
        }
        const lastMessage = {
          id: conv.last_message_id,
          content: conv.last_message_content,
          senderId: conv.last_message_sender_id,
          createdAt: conv.last_message_at || conv.updated_at
        }
        // Vérifier que l'ID de la conversation est valide
        const conversationId = conv.id || conv.conversationId || `temp-${Date.now()}`
        if (!conversationId) {
          console.warn('[Chat] Conversation sans ID valide:', conv)
          return null
        }
        
        return {
          ...conv,
          id: conversationId, // S'assurer que l'ID est défini
          client,
          mover,
          lastMessage,
          updatedAt: conv.updated_at || conv.last_message_at || new Date().toISOString(),
          unreadCount: parseInt(conv.unread_count) || 0,
          displayName: userType === 'client' 
            ? `${mover.first_name || ''} ${mover.last_name || ''}`.trim() || mover.company_name || 'Déménageur'
            : `${client.first_name || ''} ${client.last_name || ''}`.trim() || 'Client'
        }
      })

      // Filtrer les conversations valides (non null) avant de les trier et de les définir
      const validConversations = formattedConversations.filter(conv => conv != null)
      
      // Trier par date de mise à jour (la plus récente en premier)
      validConversations.sort((a, b) => {
        try {
          return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)
        } catch (error) {
          console.error('Erreur lors du tri des conversations:', error)
          return 0
        }
      })
      
      setConversations(validConversations)
    } catch (err) {
      console.error(err)
      setError("Impossible de charger les conversations")
    } finally {
      setIsLoading(false)
    }
  }, [userType])

  const loadMessages = useCallback(async (convId) => {
    if (!convId) return
    try {
      setIsLoading(true)
      setError(null)
      const msgs = await chatService.getMessages(convId)
      if (!Array.isArray(msgs)) {
        console.error('Les messages reçus ne sont pas un tableau:', msgs)
        setMessages([])
        return
      }

      const formattedMessages = msgs.map(msg => {
        const senderId = msg.sender_id || msg.sender?.id || null
        const senderName = msg.sender_name || msg.sender?.name || `${msg.sender?.first_name || ''} ${msg.sender?.last_name || ''}`.trim() || 'Utilisateur'
        const isCurrentUser = senderId === user?.id

        return {
          id: msg.id || `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          senderId,
          senderName,
          content: msg.content || msg.message || '',
          createdAt: msg.created_at || msg.createdAt || new Date().toISOString(),
          read: msg.read || msg.is_read || isCurrentUser,
          status: 'delivered'
        }
      })

      setMessages(formattedMessages)

      const unreadIds = formattedMessages.filter(m => m.senderId !== user?.id && !m.read).map(m => m.id).filter(Boolean)
      if (unreadIds.length > 0) await chatService.markAsRead(convId, unreadIds)

      setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
    } catch (err) {
      console.error(err)
      setError("Impossible de charger les messages")
    } finally {
      setIsLoading(false)
    }
  }, [user?.id])

  const handleSendMessage = async (e) => {
    e.preventDefault()
    if (!input.trim() || !conversationId) return

    const tempId = Date.now().toString()
    const tempMessage = {
      id: tempId,
      tempId,
      content: input,
      senderId: user.id,
      senderName: `${user.first_name} ${user.last_name}`.trim(),
      conversationId,
      status: "sending",
      read: false,
      createdAt: new Date().toISOString()
    }

    // Mise à jour optimiste de l'UI
    setMessages(prev => [tempMessage, ...prev])
    setInput("")

    try {
      // Envoyer le message via le service de chat
      const msg = await chatService.sendChatMessage(conversationId, input)
      
      // Mettre à jour le message avec l'ID du serveur et le marquer comme envoyé
      setMessages(prev => 
        prev.map(m => 
          m.tempId === tempId 
            ? { 
                ...m, 
                id: msg.id, 
                status: 'sent', 
                tempId: undefined,
                // S'assurer que les champs sont correctement définis
                senderId: msg.sender_id || m.senderId,
                senderName: m.senderName,
                createdAt: msg.created_at || m.createdAt,
                read: msg.read || false
              } 
            : m
        )
      )
      
      // Faire défiler vers le bas pour afficher le nouveau message
      setTimeout(() => {
        endRef.current?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
      
      // Mettre à jour la conversation avec le dernier message
      setConversations(prev => {
        const updated = [...prev]
        const convIndex = updated.findIndex(c => c.id === conversationId)
        
        if (convIndex !== -1) {
          updated[convIndex] = {
            ...updated[convIndex],
            lastMessage: {
              id: msg.id,
              content: input,
              senderId: user.id,
              createdAt: new Date().toISOString()
            },
            updatedAt: new Date().toISOString()
          }
          // Déplacer la conversation en haut de la liste
          const [moved] = updated.splice(convIndex, 1)
          updated.unshift(moved)
        }
        
        return updated
      })
      
    } catch (err) {
      console.error('Erreur lors de l\'envoi du message:', err)
      // Mettre à jour le statut du message en erreur
      setMessages(prev => 
        prev.map(m => 
          m.tempId === tempId 
            ? { ...m, status: 'error', error: 'Échec de l\'envoi. Réessayer?' } 
            : m
        )
      )
      
      // Afficher une notification d'erreur
      toast.error('Impossible d\'envoyer le message. Veuillez réessayer.')
    }
  }

  const handleInputChange = (e) => {
    const value = e.target.value
    setInput(value)
    
    if (!conversationId) return

    // Envoyer l'événement de frappe
    try {
      chatService.sendTyping(conversationId, true)
      
      // Réinitialiser le délai de fin de frappe
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
      
      // Désactiver l'indicateur après 2 secondes d'inactivité
      typingTimeoutRef.current = setTimeout(() => {
        try { 
          chatService.sendTyping(conversationId, false)
          setIsTyping(false)
        } catch (error) {
          console.error('Erreur lors de l\'envoi de l\'indicateur de frappe:', error)
        }
      }, 2000)
    } catch (error) {
      console.error('Erreur lors de l\'envoi de l\'indicateur de frappe:', error)
    }
  }

  // Gestion des messages en temps réel
  useEffect(() => {
    if (!user) return

    const setupWebSocket = async () => {
      const token = localStorage.getItem("token")
      if (!token) return

      try {
        await chatService.connect(token)

        // Écoute des nouveaux messages
        const handleNewMessage = (message) => {
          console.log('Nouveau message reçu dans Chat:', message);
          
          // Mettre à jour les messages si c'est pour la conversation courante
          if (message.conversationId === conversationId) {
            setMessages(prev => {
              // Vérifier si le message existe déjà pour éviter les doublons
              const messageExists = prev.some(m => m.id === message.id || m.tempId === message.id);
              if (messageExists) {
                console.log('Message déjà présent, ignoré:', message.id);
                return prev;
              }
              console.log('Ajout du nouveau message à la liste des messages');
              return [message, ...prev];
            });
          }
          
          // Mettre à jour la liste des conversations avec le dernier message
          setConversations(prev => {
            const updated = [...prev];
            const convIndex = updated.findIndex(c => c && c.id === message.conversationId);
            
            if (convIndex !== -1) {
              const updatedConversation = {
                ...updated[convIndex],
                lastMessage: {
                  id: message.id,
                  content: message.content,
                  senderId: message.senderId,
                  createdAt: message.createdAt || new Date().toISOString()
                },
                updatedAt: message.createdAt || new Date().toISOString(),
                unreadCount: message.conversationId === conversationId ? 0 : (updated[convIndex].unreadCount || 0) + 1
              };
              
              // Mettre à jour la conversation
              updated[convIndex] = updatedConversation;
              
              // Déplacer la conversation en haut de la liste
              const [moved] = updated.splice(convIndex, 1);
              updated.unshift(moved);
              
              console.log('Conversation mise à jour avec le nouveau message:', updatedConversation);
            } else {
              console.log('Conversation non trouvée pour le message, chargement des conversations...');
              // Si la conversation n'existe pas, recharger la liste des conversations
              loadConversations();
            }
            
            return updated;
          });
          
          // Faire défiler vers le bas pour afficher le nouveau message
          setTimeout(() => {
            endRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        };
        
        // Écouter les événements de nouveau message via l'API des événements du navigateur
        const handleNewMessageEvent = (event) => {
          if (event.detail) {
            handleNewMessage(event.detail);
          }
        };
        
        // S'abonner à l'événement personnalisé
        window.addEventListener('newMessage', handleNewMessageEvent);

        // Gestion des indicateurs de frappe
        const handleTyping = ({ conversationId: typingConvId, userId, isTyping: userIsTyping }) => {
          if (typingConvId === conversationId) {
            setIsTyping(userIsTyping)
          }
        }

        // S'abonner aux événements
        chatService.on('message', handleNewMessage)
        chatService.on('typing', handleTyping)

        // Nettoyage
        return () => {
          chatService.off('message', handleNewMessage);
          chatService.off('typing', handleTyping);
          window.removeEventListener('newMessage', handleNewMessageEvent);
        }
      } catch (error) {
        console.error('Erreur de connexion WebSocket:', error)
      }
    }

    setupWebSocket()
    loadConversations()

    // Nettoyage
    return () => {
      // On ne déconnecte pas pour maintenir la connexion WebSocket
    }
  }, [user, conversationId, loadConversations])

  // Charger les messages et marquer comme lus lors du changement de conversation
  useEffect(() => {
    if (!conversationId) return
    
    const loadAndMarkAsRead = async () => {
      try {
        await loadMessages(conversationId)
        // Marquer tous les messages non lus comme lus
        const unreadMessages = messages.filter(m => !m.read && m.senderId !== user?.id)
        if (unreadMessages.length > 0) {
          const unreadIds = unreadMessages.map(m => m.id).filter(Boolean)
          await chatService.markAsRead(conversationId, unreadIds)
          
          // Mettre à jour l'état local
          setMessages(prev => 
            prev.map(msg => 
              unreadIds.includes(msg.id) ? { ...msg, read: true } : msg
            )
          )
          
          // Mettre à jour le compteur de messages non lus dans la liste des conversations
          setConversations(prev => 
            prev.map(conv => 
              conv.id === conversationId 
                ? { ...conv, unreadCount: 0 }
                : conv
            )
          )
        }
      } catch (error) {
        console.error('Erreur lors du chargement/marquage des messages comme lus:', error)
      }
    }
    
    loadAndMarkAsRead()
    
    // S'abonner aux événements de lecture
    const handleMessageRead = ({ messageIds, conversationId: readConvId }) => {
      if (readConvId !== conversationId) return
      
      setMessages(prev => 
        prev.map(msg => 
          messageIds.includes(msg.id) ? { ...msg, read: true } : msg
        )
      )
    }
    
    chatService.on('messageRead', handleMessageRead)
    
    return () => {
      chatService.off('messageRead', handleMessageRead)
    }
  }, [conversationId, loadMessages, messages, user?.id])

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  if (!user) return <div className="p-4">Veuillez vous connecter pour accéder au chat</div>
  if (isLoading && !conversationId) return <div className="flex items-center justify-center h-64">Chargement des conversations...</div>
  if (error) return <div className="p-4 text-red-500">{error}</div>

  const MessageBubble = ({ message }) => {
    const isCurrentUser = message.senderId === user.id
    const bubbleStyle = isCurrentUser 
      ? styles.messageBubble?.client || "bg-amber-500 text-white rounded-br-md"
      : styles.messageBubble?.other || "bg-white text-gray-800 border border-gray-200 rounded-bl-md"
    
    const senderNameStyle = styles.senderName || "text-xs font-medium text-amber-600 mb-1"
    const timeStyle = styles.timeText || "text-xs text-gray-500"
    const statusStyle = {
      sending: styles.statusSending || "text-gray-400",
      error: styles.statusError || "text-red-500"
    }

    return (
      <div 
        className={`flex ${isCurrentUser ? "justify-end" : "justify-start"} mb-4`} 
        key={message.id || message.tempId}
      >
        <div className={`max-w-xs lg:max-w-md px-4 py-3 rounded-2xl shadow-lg ${bubbleStyle}`}>
          {!isCurrentUser && (
            <div className={senderNameStyle}>
              {message.senderName}
            </div>
          )}
          <div className="text-sm">{message.content}</div>
          <div className="text-xs mt-1 flex justify-between">
            <span className={timeStyle}>
              {formatDate(message.createdAt)}
            </span>
            {message.status === 'sending' && (
              <span className={statusStyle.sending}>Envoi...</span>
            )}
            {message.status === 'error' && (
              <span className={statusStyle.error}>Erreur</span>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Styles par défaut si non fournis
  const defaultStyles = {
    container: "flex h-screen bg-gray-100",
    sidebar: "w-80 border-r bg-white overflow-y-auto",
    sidebarHeader: "p-4 border-b sticky top-0 bg-white z-10",
    sidebarTitle: "text-lg font-semibold text-gray-800"
  }

  // Fusionner les styles personnalisés avec les styles par défaut
  const mergedStyles = {
    container: `${defaultStyles.container} ${styles.container || ''}`,
    sidebar: `${defaultStyles.sidebar} ${styles.sidebar || ''}`,
    sidebarHeader: `${defaultStyles.sidebarHeader} ${styles.sidebarHeader || ''}`,
    sidebarTitle: `${defaultStyles.sidebarTitle} ${styles.sidebarTitle || ''}`,
    ...styles
  }

  // Fonction de rendu d'une conversation individuelle
  const renderConversationItem = (conv) => {
    if (!conv || !conv.id) {
      console.warn('Conversation invalide:', conv);
      return null;
    }

    try {
      const otherUser = userType === "client" ? (conv.mover || {}) : (conv.client || {});
      const displayName = conv.displayName || 
                         (userType === 'client' 
                           ? `${otherUser.first_name || ''} ${otherUser.last_name || ''}`.trim() || otherUser.company_name || 'Déménageur'
                           : `${otherUser.first_name || ''} ${otherUser.last_name || ''}`.trim() || 'Client');

      const lastMessageContent = conv.lastMessage?.content || "Aucun message";
      const lastMessageDate = conv.lastMessage?.createdAt || conv.updatedAt;

      return (
        <div
          key={conv.id}
          className={`p-4 hover:bg-gray-700 cursor-pointer transition-colors duration-200 ${
            conversationId === conv.id ? "bg-gray-700 border-r-2 border-amber-500" : ""
          }`}
          onClick={() => {
            if (conv.id) {
              navigate(`${basePath}/chat/${conv.id}`);
              loadMessages(conv.id);
            }
          }}
        >
          <div className="flex justify-between items-start">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-100 truncate">{displayName}</p>
              <p className="text-sm text-gray-400 truncate mt-1">{lastMessageContent}</p>
            </div>
            <div className="ml-4 flex flex-col items-end">
              <p className="text-xs text-gray-500">{formatDate(lastMessageDate)}</p>
              {conv.unreadCount > 0 && (
                <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500 text-gray-900 mt-1">
                  {Math.min(conv.unreadCount, 99) + (conv.unreadCount > 99 ? '+' : '')}
                </span>
              )}
            </div>
          </div>
        </div>
      );
    } catch (error) {
      console.error('Erreur lors du rendu de la conversation:', error, conv);
      return null;
    }
  };

  return (
    <div className={mergedStyles.container}>
      {/* Conversations */}
      <div className={mergedStyles.sidebar}>
        <div className={mergedStyles.sidebarHeader}>
          <h2 className={mergedStyles.sidebarTitle}>Conversations</h2>
        </div>
        <div className="divide-y divide-gray-700">
          {!isLoading && conversations.length === 0 ? (
            <div className="p-4 text-gray-400">Aucune conversation</div>
          ) : (
            conversations.map(renderConversationItem).filter(Boolean)
          )}
        </div>
      </div>

      {/* Chat */}
      <div className="flex-1 flex flex-col bg-white">
        {conversationId ? (
          <>
            <div className="p-4 border-b bg-white">
              <h2 className="text-lg font-semibold text-gray-800">
                {(() => {
                  try {
                    const currentConv = conversations.find(c => c && c.id === conversationId);
                    return currentConv?.displayName || 'Chat';
                  } catch (error) {
                    console.error('Erreur lors de la récupération du nom de la conversation:', error);
                    return 'Chat';
                  }
                })()}
              </h2>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-2 bg-gray-50">
              {messages.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-400">
                  {isLoading ? 'Chargement des messages...' : 'Commencez la conversation !'}
                </div>
              ) : (
                messages
                  .filter(msg => msg && (msg.id || msg.tempId)) // Filtrer les messages invalides
                  .map(msg => (
                    <MessageBubble 
                      key={msg.id || msg.tempId} 
                      message={{
                        id: msg.id || msg.tempId,
                        senderId: msg.senderId || null,
                        senderName: msg.senderName || 'Utilisateur inconnu',
                        content: msg.content || '',
                        createdAt: msg.createdAt || new Date().toISOString(),
                        read: msg.read !== false,
                        status: msg.status || 'delivered'
                      }} 
                    />
                  ))
              )}
              <div ref={endRef} />
              {isTyping && (
                <div className="flex items-center space-x-2 px-4 text-gray-500">
                  <span className="h-2 w-2 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '0ms' }}></span>
                  <span className="h-2 w-2 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '150ms' }}></span>
                  <span className="h-2 w-2 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  <span className="text-sm ml-2">En train d'écrire...</span>
                </div>
              )}
            </div>
            <form 
              onSubmit={handleSendMessage} 
              className="p-4 border-t bg-white flex space-x-3"
            >
              <input 
                type="text" 
                value={input} 
                onChange={handleInputChange} 
                placeholder="Tapez votre message..." 
                className="flex-1 border border-gray-300 rounded-xl px-4 py-3 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button 
                type="submit" 
                disabled={!input.trim()} 
                className="px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Envoyer
              </button>
            </form>
          </>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400">Sélectionnez une conversation pour commencer</div>
        )}
      </div>
    </div>
  )
}

Chat.propTypes = {
  userType: PropTypes.oneOf(['client', 'demenageur']),
  styles: PropTypes.shape({
    container: PropTypes.string,
    sidebar: PropTypes.string,
    sidebarHeader: PropTypes.string,
    sidebarTitle: PropTypes.string,
    messageBubble: PropTypes.shape({
      client: PropTypes.string,
      other: PropTypes.string
    }),
    senderName: PropTypes.string,
    timeText: PropTypes.string,
    statusSending: PropTypes.string,
    statusError: PropTypes.string
  })
}

export default Chat
